import { FilterAltOffOutlined, FilterAltOutlined } from "@mui/icons-material"
import {
  Box,
  Button,
  darken,
  IconButton,
  Stack,
  Theme,
  Tooltip,
  useMediaQuery,
} from "@mui/material"
import { lighten } from "@mui/material"
import { useStore } from "@nanostores/react"
import React, { useMemo } from "react"

import { getPoolGroupId, PoolsTableRow } from "./PoolsTableRow"
import type { Pool } from "../../api/pools-api"
import { AsyncTable } from "../../components/AsyncTable"
import { SwitchCurrencyButton } from "../../components/SwitchCurrencyButton"
import { $userSettings } from "../../stores/user-settings-store"
import { calcPercentChange } from "../../utils/utils"
import { getOverview } from "@/api/monitor-api"
import { LoadingSpinner } from "@/components/LoadingSpinner"
import { $poolsOverviewFull, $poolsOverviewMap } from "@/stores/pools-store"
import { PoolOverview } from "@/types"

export type PoolsTableProps = {
  pools: Pool[]
  overviewMap: Record<string, PoolOverview> | null
}

export function PoolsTable(props: PoolsTableProps) {
  const { pools: allPools, overviewMap } = props

  const [loadingMore, setLoadingMore] = React.useState(false)
  const poolsOverviewFull = useStore($poolsOverviewFull)

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"))
  const [showFavorites, setShowFavorites] = React.useState(false)
  const userSettings = useStore($userSettings)

  const poolGroupsMap = useMemo(() => {
    const map = allPools.reduce(
      (acc, pool) => {
        const key = getPoolGroupId(pool)
        if (!acc[key]) {
          acc[key] = []
        }

        acc[key].push(pool)
        if (overviewMap) {
          acc[key] = acc[key].sort((a, b) => {
            return overviewMap[a.id].liquidityUsd > overviewMap[b.id].liquidityUsd ? -1 : 1
          })
        }
        return acc
      },
      {} as Record<string, Pool[]>,
    )
    console.log("onPoolGroupsMap", map)
    return map
  }, [allPools, overviewMap])

  const poolGroups = useMemo(() => {
    if (!userSettings || !showFavorites) return Object.values(poolGroupsMap)

    return userSettings.watchlist
      .map((poolGroupId) => {
        return poolGroupsMap[poolGroupId]
      })
      .filter(Boolean)
  }, [poolGroupsMap, userSettings, showFavorites])

  if (!overviewMap) {
    return <LoadingSpinner />
  }

  return (
    <Stack gap={1}>
      <Box
        sx={(theme) => ({
          background: "var(--mui-palette-paperAlt)",
          overflow: "auto",

          "& th:nth-child(2)": {
            background: "var(--mui-palette-paperAlt)",
            position: "sticky",
            zIndex: 1,
            left: 0,
            // borderRight: "1px solid var(--mui-palette-divider)",
          },
          "& td:nth-child(2)": {
            background: "var(--mui-palette-paperAlt)",
            position: "sticky",
            zIndex: 1,
            left: 0,
            // borderRight: "1px solid var(--mui-palette-divider)",
          },
          "& tr:hover>td:nth-child(2)": {
            background:
              theme.palette.mode === "dark"
                ? lighten(theme.palette.paperAlt, 0.08)
                : darken(theme.palette.paperAlt, 0.033),
          },
        })}
      >
        <AsyncTable
          renderRow={(row: Pool[]) => (
            <PoolsTableRow key={getPoolGroupId(row[0])} row={row} overviewMap={overviewMap} />
          )}
          headerCells={
            isMobile
              ? []
              : [
                  {
                    label: (
                      <Stack alignItems="center">
                        <Tooltip title={showFavorites ? "Show all" : "Show favorites"}>
                          <IconButton
                            size="small"
                            color="secondary"
                            sx={{ marginX: -1 }}
                            onClick={() => setShowFavorites(!showFavorites)}
                          >
                            {showFavorites ? (
                              <FilterAltOffOutlined fontSize="small" />
                            ) : (
                              <FilterAltOutlined fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ),
                    sx: { minWidth: 40, width: 40, maxWidth: 40, paddingRight: 0 },
                  },
                  {
                    label: (
                      <Stack direction="row" gap={1} sx={{ marginY: -1 }} alignItems="center">
                        <span>Token</span>
                      </Stack>
                    ),
                    sx: { width: "100%" },
                  },
                  {
                    label: "Available DEX's",
                    sx: { minWidth: 140 },
                  },
                  {
                    label: (
                      <Stack
                        direction="row"
                        gap={1}
                        sx={{ marginY: -1 }}
                        alignItems="center"
                        justifyContent="flex-end"
                      >
                        <span>Price</span>
                        <SwitchCurrencyButton />
                      </Stack>
                    ),
                    align: "right",
                    // sortable: true,
                    field: "price",
                    sx: { minWidth: 160 },
                  },
                  {
                    sortable: true,
                    label: "24h Txns",
                    field: "transactions",
                    align: "right",
                    sx: { minWidth: 120 },
                  },
                  {
                    sortable: true,
                    align: "right",
                    label: "24h Vol",
                    field: "volume",
                    sx: { minWidth: 120 },
                  },
                  {
                    sortable: true,
                    label: "5m",
                    field: "5m",
                    sx: { minWidth: 90 },
                  },
                  {
                    sortable: true,
                    field: "1h",
                    label: "1h",
                    sx: { minWidth: 90 },
                  },
                  {
                    sortable: true,
                    field: "6h",
                    label: "6h",
                    sx: { minWidth: 90 },
                  },
                  {
                    sortable: true,
                    field: "24h",
                    label: "24h",
                    sx: { minWidth: 90 },
                  },
                  {
                    // sortable: true,
                    field: "liquidity",
                    align: "right",
                    label: "Liquidity",
                    sx: { minWidth: 140 },
                  },
                  {
                    sortable: false,
                    field: "marketCap",
                    label: "Market Cap",
                    align: "right",
                    sx: { minWidth: 140 },
                  },
                ]
          }
          initialSortDir="desc"
          initialSortField="volume"
          pageSize={30}
          fetchFunction={async (offset, ascending, sortField) => {
            if (offset > poolGroups.length) return []

            let valueSelector: any = () => 0

            if (sortField === "volume") {
              valueSelector = (pool: Pool) => overviewMap[pool.id].volumeUsd
            } else if (sortField === "transactions") {
              valueSelector = (pool: Pool) => overviewMap[pool.id].transactions
            } else if (sortField === "price") {
              valueSelector = (pool: Pool) => overviewMap[pool.id].latestPrice
            } else if (sortField === "marketCap") {
              valueSelector = (pool: Pool) => overviewMap[pool.id].marketCap
            } else if (sortField === "5m") {
              valueSelector = (pool: Pool) =>
                calcPercentChange(
                  overviewMap[pool.id]?.price5mAgo,
                  overviewMap[pool.id]?.latestPrice,
                )
            } else if (sortField === "1h") {
              valueSelector = (pool: Pool) =>
                calcPercentChange(
                  overviewMap[pool.id]?.price1hAgo,
                  overviewMap[pool.id]?.latestPrice,
                )
            } else if (sortField === "6h") {
              valueSelector = (pool: Pool) =>
                calcPercentChange(
                  overviewMap[pool.id]?.price6hAgo,
                  overviewMap[pool.id]?.latestPrice,
                )
            } else if (sortField === "24h") {
              valueSelector = (pool: Pool) =>
                calcPercentChange(
                  overviewMap[pool.id]?.price24hAgo,
                  overviewMap[pool.id]?.latestPrice,
                )
            } else {
              valueSelector = (pool: Pool) => pool[sortField as keyof Pool]
            }

            const sortedPools = poolGroups.sort((a, b) => {
              const valueA = a.reduce((acc, pool) => acc + valueSelector(pool) || 0, 0)
              const valueB = b.reduce((acc, pool) => acc + valueSelector(pool) || 0, 0)

              if (valueA < valueB) {
                return ascending ? -1 : 1
              }
              if (valueA > valueB) {
                return ascending ? 1 : -1
              }

              return 0
            })

            return sortedPools.slice(offset, offset + 30)
          }}
        />
      </Box>
      {!poolsOverviewFull && (
        <Button
          onClick={() => {
            setLoadingMore(true)
            getOverview(undefined, 0).then((overview) => {
              const map = overview.reduce(
                (acc, overview) => ({
                  ...acc,
                  [overview.poolId]: overview,
                }),
                {} as Record<string, PoolOverview>,
              )

              $poolsOverviewMap.set(map)
              $poolsOverviewFull.set(true)
            })
          }}
          disabled={loadingMore}
          variant="outlined"
          color="inherit"
          size="small"
          sx={{
            alignSelf: "center",
            textTransform: "none",
          }}
        >
          {loadingMore ? "Loading more records..." : "Load more records"}
        </Button>
      )}
    </Stack>
  )
}
